import React, { createContext, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { countryConfigApi, CountryConfigData } from '../api/countryConfigApi';
import { formatCurrency as formatCurrencyBase, CountryCode } from '../config/currency';

interface CountryContextValue {
  country: CountryCode;
  currencySymbol: string;
  currencyCode: string;
  taxLabel: string;
  taxSublabels: string[];
  locale: string;
  defaultAggregators: string[];
  /** Pre-bound currency formatter using vendor's country */
  formatAmount: (amount: number | string, options?: { showSymbol?: boolean; showDecimals?: boolean; compact?: boolean }) => string;
  isLoading: boolean;
  updateCountry: (country: string) => Promise<void>;
}

const defaultValue: CountryContextValue = {
  country: 'IN',
  currencySymbol: '₹',
  currencyCode: 'INR',
  taxLabel: 'GST',
  taxSublabels: ['CGST', 'SGST', 'IGST'],
  locale: 'en-IN',
  defaultAggregators: ['Zomato', 'Swiggy'],
  formatAmount: (amount, options) => formatCurrencyBase(amount, { ...options, country: 'IN' }),
  isLoading: false,
  updateCountry: async () => {},
};

const CountryContext = createContext<CountryContextValue>(defaultValue);

export function CountryProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<CountryConfigData>({
    queryKey: ['countryConfig'],
    queryFn: countryConfigApi.getCountryConfig,
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
  });

  const value = useMemo<CountryContextValue>(() => {
    const country = (data?.country || 'IN') as CountryCode;
    return {
      country,
      currencySymbol: data?.currencySymbol || '₹',
      currencyCode: data?.currencyCode || 'INR',
      taxLabel: data?.taxLabel || 'GST',
      taxSublabels: data?.taxSublabels || ['CGST', 'SGST', 'IGST'],
      locale: data?.locale || 'en-IN',
      defaultAggregators: data?.defaultAggregators || ['Zomato', 'Swiggy'],
      formatAmount: (amount, options) =>
        formatCurrencyBase(amount, { ...options, country }),
      isLoading,
      updateCountry: async (newCountry: string) => {
        await countryConfigApi.updateCountry(newCountry);
        queryClient.invalidateQueries({ queryKey: ['countryConfig'] });
      },
    };
  }, [data, isLoading, queryClient]);

  return (
    <CountryContext.Provider value={value}>
      {children}
    </CountryContext.Provider>
  );
}

export { CountryContext };
export default CountryContext;
